import React from "react";
import { Alert, AlertIcon, Box, Grid, GridItem, Text, useBreakpointValue, useColorModeValue } from "@chakra-ui/react";
import RiskReviewComponentPalette from "./RiskReviewComponentPalette";
import RiskReviewTemplateCanvas from "./RiskReviewTemplateCanvas";
import RiskReviewFieldEditor from "./RiskReviewFieldEditor";
import RiskReviewSectionEditor from "./RiskReviewSectionEditor";
import RiskReviewRuleEditor from "./RiskReviewRuleEditor";

const slugCode = (value, fallback) =>
  (value || fallback || "FIELD")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const createField = (component, sequence) => {
  const label = component.label || "Field";
  return {
    code: `${slugCode(label)}_${Date.now().toString().slice(-5)}`,
    label,
    inputType: component.inputType || "TEXT",
    sequence,
    required: false,
    readOnly: Boolean(component.readOnly),
    hidden: false,
    options: component.options
      ? [
          { label: "Option 1", value: "OPTION_1", sequence: 1, active: true },
          { label: "Option 2", value: "OPTION_2", sequence: 2, active: true },
        ]
      : [],
    validations: [],
    rules: [],
    dataSource: { type: "MANUAL" },
    uiConfig: { width: component.width || 6 },
  };
};

const RiskReviewTemplateBuilder = ({ version, onChange, isReadOnly }) => {
  const canUseBuilder = useBreakpointValue({ base: false, xl: true });
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const [selected, setSelected] = React.useState({ type: "section", code: version?.sections?.[0]?.code });

  React.useEffect(() => {
    if (!selected.code && version?.sections?.[0]?.code) {
      setSelected({ type: "section", code: version.sections[0].code });
    }
  }, [selected.code, version?.sections]);

  if (!canUseBuilder) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        This template builder requires a larger screen.
      </Alert>
    );
  }

  const updateSections = (updater) => {
    const sections = updater([...(version?.sections || [])]);
    onChange?.({ ...version, sections });
  };

  const addSection = () => {
    const sequence = (version?.sections || []).length + 1;
    const section = {
      code: `SECTION_${Date.now().toString().slice(-5)}`,
      name: `Section ${sequence}`,
      description: "",
      sequence,
      collapsible: true,
      defaultExpanded: true,
      required: false,
      active: true,
      fields: [],
    };
    updateSections((sections) => [...sections, section]);
    setSelected({ type: "section", code: section.code });
  };

  const addField = (component, sectionCode) => {
    const targetSectionCode = sectionCode || (selected.type === "section" ? selected.code : selected.sectionCode) || version?.sections?.[0]?.code;
    if (!targetSectionCode) {
      addSection();
      return;
    }
    let created;
    updateSections((sections) =>
      sections.map((section) => {
        if (section.code !== targetSectionCode) return section;
        created = createField(component || { label: "Text", inputType: "TEXT" }, (section.fields || []).length + 1);
        return { ...section, fields: [...(section.fields || []), created] };
      }),
    );
    if (created) setSelected({ type: "field", sectionCode: targetSectionCode, code: created.code });
  };

  const changeSection = (key, value) => {
    updateSections((sections) =>
      sections.map((section) =>
        section.code === selected.code ? { ...section, [key]: value } : section,
      ),
    );
    if (key === "code") setSelected({ type: "section", code: value });
  };

  const changeField = (key, value) => {
    updateSections((sections) =>
      sections.map((section) => {
        if (section.code !== selected.sectionCode) return section;
        return {
          ...section,
          fields: (section.fields || []).map((field) => {
            if (field.code !== selected.code) return field;
            if (key === "uiConfig.width") {
              return { ...field, uiConfig: { ...(field.uiConfig || {}), width: value } };
            }
            return { ...field, [key]: value };
          }),
        };
      }),
    );
    if (key === "code") setSelected((current) => ({ ...current, code: value }));
  };

  const changeOption = (index, key, value) => {
    updateSections((sections) =>
      sections.map((section) => {
        if (section.code !== selected.sectionCode) return section;
        return {
          ...section,
          fields: (section.fields || []).map((field) => {
            if (field.code !== selected.code) return field;
            const options = [...(field.options || [])];
            options[index] = { ...options[index], [key]: value };
            return { ...field, options };
          }),
        };
      }),
    );
  };

  const addOption = () => {
    updateSections((sections) =>
      sections.map((section) => {
        if (section.code !== selected.sectionCode) return section;
        return {
          ...section,
          fields: (section.fields || []).map((field) => {
            if (field.code !== selected.code) return field;
            const next = (field.options || []).length + 1;
            return {
              ...field,
              options: [...(field.options || []), { label: `Option ${next}`, value: `OPTION_${next}`, sequence: next, active: true }],
            };
          }),
        };
      }),
    );
  };

  const deleteField = () => {
    updateSections((sections) =>
      sections.map((section) =>
        section.code === selected.sectionCode
          ? { ...section, fields: (section.fields || []).filter((field) => field.code !== selected.code) }
          : section,
      ),
    );
    setSelected({ type: "section", code: selected.sectionCode });
  };

  const moveSection = (sectionCode, direction) => {
    updateSections((sections) => {
      const ordered = [...sections].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
      const index = ordered.findIndex((section) => section.code === sectionCode);
      const target = index + direction;
      if (target < 0 || target >= ordered.length) return sections;
      [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
      return ordered.map((section, nextIndex) => ({ ...section, sequence: nextIndex + 1 }));
    });
  };

  const moveField = (sectionCode, fieldCode, direction) => {
    updateSections((sections) =>
      sections.map((section) => {
        if (section.code !== sectionCode) return section;
        const ordered = [...(section.fields || [])].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        const index = ordered.findIndex((field) => field.code === fieldCode);
        const target = index + direction;
        if (target < 0 || target >= ordered.length) return section;
        [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
        return { ...section, fields: ordered.map((field, nextIndex) => ({ ...field, sequence: nextIndex + 1 })) };
      }),
    );
  };

  const selectedSection =
    selected.type === "section"
      ? version?.sections?.find((section) => section.code === selected.code)
      : version?.sections?.find((section) => section.code === selected.sectionCode);
  const selectedField = selected.type === "field"
    ? selectedSection?.fields?.find((field) => field.code === selected.code)
    : null;

  return (
    <Grid templateColumns="280px minmax(0, 1fr) 340px" gap={4} alignItems="start">
      <GridItem>
        <RiskReviewComponentPalette onAddField={(component) => addField(component)} isDisabled={isReadOnly} />
      </GridItem>
      <GridItem>
        <RiskReviewTemplateCanvas
          version={version}
          selected={selected}
          isReadOnly={isReadOnly}
          onSelectSection={(code) => setSelected({ type: "section", code })}
          onSelectField={(sectionCode, code) => setSelected({ type: "field", sectionCode, code })}
          onAddSection={addSection}
          onAddField={(sectionCode) => addField({ label: "Text", inputType: "TEXT" }, sectionCode)}
          onMoveSection={moveSection}
          onMoveField={moveField}
        />
      </GridItem>
      <GridItem bg={bg} borderWidth="1px" borderColor={borderColor} borderRadius="md" p={4} boxShadow="sm" position="sticky" top={4}>
        <Text fontWeight="semibold" mb={3}>
          Properties
        </Text>
        {selectedField ? (
          <RiskReviewFieldEditor
            field={selectedField}
            onChange={changeField}
            onAddOption={addOption}
            onOptionChange={changeOption}
            onDelete={deleteField}
            isReadOnly={isReadOnly}
          />
        ) : (
          <RiskReviewSectionEditor section={selectedSection} onChange={changeSection} isReadOnly={isReadOnly} />
        )}
        <Box mt={4}>
          <RiskReviewRuleEditor />
        </Box>
      </GridItem>
    </Grid>
  );
};

export default RiskReviewTemplateBuilder;
